// Dati di Users V2: utenti, ruoli, assegnazioni progetto/team di un utente, creazione e modifica.
import { API_ENDPOINTS } from '../config/api.config';

async function request(url, options) {
  const res = await fetch(url, options);
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) throw new Error(body?.error || body?.message || `Request failed (${res.status})`);
  return body || {};
}

const jsonOptions = (method, payload) => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(payload),
});

/** Utenti con ruolo, ruolo gerarchico e people manager, più gli elenchi dei ruoli. */
export async function fetchUsersBundle() {
  const [u, r, pr] = await Promise.all([request(API_ENDPOINTS.USERS), request(API_ENDPOINTS.ROLES), request(API_ENDPOINTS.PEOPLE_ROLES)]);
  return { users: u.users || [], roles: r.roles || [], peopleRoles: pr.people_roles || [] };
}

/**
 * Progetti dell'utente (assegnazioni attive e team), uno per progetto:
 * ruoli sul progetto e tutti i team dell'utente su quel progetto.
 */
export async function fetchUserAssignments(userId) {
  const [t, p] = await Promise.all([
    request(`${API_ENDPOINTS.TEAMS}?user_id=${userId}&get_user_memberships=true`),
    request(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}`),
  ]);
  const byProject = new Map();
  const rowFor = (projectId, name, customer, since) => {
    const key = String(projectId);
    if (!byProject.has(key)) byProject.set(key, { projectId, project: name || '', customer: customer || '', roles: [], teams: [], since });
    return byProject.get(key);
  };
  (p.user_projects_with_references || []).forEach((a) => {
    const row = rowFor(a.project?.id, a.project?.name, a.customer?.name, a.assigned_at);
    const role = a.project_role?.name;
    if (role && !row.roles.includes(role)) row.roles.push(role);
  });
  (t.teams || []).forEach((team) => {
    const row = rowFor(team.project_id, team.project_name, '', team.membership_created_at);
    if (team.name && !row.teams.includes(team.name)) row.teams.push(team.name);
  });
  return [...byProject.values()].sort((a, b) => a.project.localeCompare(b.project));
}

export const createUser = (payload) => request(API_ENDPOINTS.USERS, jsonOptions('POST', payload));
export const updateUser = (id, payload) => request(`${API_ENDPOINTS.USERS}?id=${id}`, jsonOptions('PUT', payload));

const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/** Errori del form utente per campo (stesse regole della pagina classica); oggetto vuoto se valido. */
export function validateUserForm(form, users, editingId) {
  const e = {};
  const email = form.email.trim().toLowerCase();
  if (!form.username.trim()) e.username = 'Required';
  if (!email) e.email = 'Required';
  else if (!EMAIL_RE.test(email) || email.length > 254 || email.includes('..') || email.startsWith('.') || email.endsWith('.')) e.email = 'Invalid email address';
  else if (users.some((u) => String(u.email || '').toLowerCase() === email && String(u.id) !== String(editingId))) e.email = 'Another user already uses this email';
  if (!form.role_id) e.role_id = 'Required';
  if (!form.org_role_id) e.org_role_id = 'Required';
  if (!form.id_people_manager) e.id_people_manager = 'Required';
  return e;
}

/** Id di tutti i riporti (diretti e indiretti) di un utente; tollera cicli. */
export function descendantIds(users, rootId) {
  const children = new Map();
  users.forEach((u) => {
    const key = String(u.id_people_manager);
    if (!children.has(key)) children.set(key, []);
    children.get(key).push(String(u.id));
  });
  const out = new Set();
  const stack = [String(rootId)];
  while (stack.length) {
    (children.get(stack.pop()) || []).forEach((id) => {
      if (!out.has(id) && id !== String(rootId)) {
        out.add(id);
        stack.push(id);
      }
    });
  }
  return out;
}
